import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, tap, from, switchMap, of, map, catchError } from 'rxjs';
import { Agent } from "../models/agent.model";
import { UserDocumentType } from './wpa/db.service';
import { jwtDecode, JwtPayload } from "jwt-decode";
import { MissionResponseDto } from '../api/models/mission-response-dto';
import { MissionService } from './mission.service';
import { OfflineService } from './wpa/offline.service';
import { DatabaseService } from './wpa/db.service';

export type { MissionResponseDto };

/**
 * Clé primaire fixe sous laquelle la mission active est mise en cache dans la
 * collection `missions`. Distincte des ids de missions chargées (UUID via
 * downloadMission) → aucune collision, et on ne vide plus la collection.
 */
const ACTIVE_MISSION_KEY = 'active';

export interface DecodedToken extends JwtPayload {
    sub: string;
    first_name: string;
    last_name: string;
    email: string;
    roles: string[];
    permissions: string[];
    temporal:boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private missionService = inject(MissionService);
    private offlineService = inject(OfflineService);
    private databaseService = inject(DatabaseService);

    private currentUserSubject = new BehaviorSubject<Agent | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();

    private activeMissionSubject = new BehaviorSubject<MissionResponseDto | null>(null);
    public activeMission$: Observable<MissionResponseDto | null> = this.activeMissionSubject.asObservable();

    get activeMission(): MissionResponseDto | null {
        return this.activeMissionSubject.getValue();
    }

    loadActiveMission(): Observable<MissionResponseDto | null> {
        // Check connectivity first, then either fetch from server or from local DB.
        return from(this.offlineService.checkConnectivity()).pipe(
            switchMap(online => {
                if (online) {
                    return this.missionService.getActiveMission().pipe(
                        tap(mission => {
                            this.activeMissionSubject.next(mission);
                            // Cache local non bloquant, clé fixe + incrementalUpsert
                            // (sérialisé par doc) : plus de course "delete-all + insert"
                            // ni de sentinel "unknown" → fini les CONFLICT 409.
                            void this.persistActiveMission(mission);
                        })
                    );
                }

                // Hors-ligne : mission active cachée, ou repli sur les missions chargées.
                return from(this.resolveOfflineActiveMission()).pipe(
                    map(mission => {
                        this.activeMissionSubject.next(mission);
                        return mission;
                    })
                );
            }),
            catchError(err => {
                console.error('Error loading active mission', err);
                this.activeMissionSubject.next(null);
                return of(null);
            })
        );
    }

    /**
     * Détermine la mission active hors-ligne :
     *  1. la mission explicitement mise en cache sous ACTIVE_MISSION_KEY (préparée en ligne) ;
     *  2. sinon, repli sur les missions chargées localement (db.bacheliers) : on retient
     *     celle qui couvre la date du jour (à défaut, la plus récemment chargée).
     * Ainsi « charger la mission du jour » suffit à délivrer hors-ligne, même si l'agent
     * n'a jamais ouvert « Délivrer » en ligne (où le cache 'active' aurait été écrit).
     */
    private async resolveOfflineActiveMission(): Promise<MissionResponseDto | null> {
        const db = this.databaseService.getDb();

        const cachedDoc = await db.missions.findOne(ACTIVE_MISSION_KEY).exec();
        const cached = (cachedDoc as any)?.mission ?? null;
        if (cached) return cached;

        // Repli : reconstruire depuis les missions chargées
        const docs = await db.bacheliers.find().exec();
        const missions = docs
            .map((d: any) => ({ ...(d.mission ?? {}), id: d.mission?.id ?? d.missionId, savedAt: d.savedAt }))
            .filter((m: any) => !!m.id);
        if (missions.length === 0) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const couvreAujourdhui = (m: any) => {
            const debut = this.parseMissionDate(m.dateDebut);
            const fin = this.parseMissionDate(m.dateFin);
            debut?.setHours(0, 0, 0, 0);
            fin?.setHours(0, 0, 0, 0);
            return (!debut || debut <= today) && (!fin || fin >= today);
        };

        // Mission couvrant aujourd'hui (celle finissant le plus tôt en priorité)…
        const dujour = missions
            .filter(couvreAujourdhui)
            .sort((a: any, b: any) =>
                (this.parseMissionDate(a.dateFin)?.getTime() ?? Infinity)
                - (this.parseMissionDate(b.dateFin)?.getTime() ?? Infinity));
        if (dujour.length > 0) return dujour[0];

        // …sinon la plus récemment chargée.
        return missions.sort((a: any, b: any) => (b.savedAt ?? '').localeCompare(a.savedAt ?? ''))[0];
    }

    /** Parse une date de mission (format backend dd/MM/yyyy ou ISO). */
    private parseMissionDate(s?: string): Date | null {
        if (!s) return null;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
            const [d, m, y] = s.split('/');
            return new Date(+y, +m - 1, +d);
        }
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    }

    /**
     * Met en cache la mission active sous la clé fixe ACTIVE_MISSION_KEY.
     * - mission présente → incrementalUpsert (idempotent, conflict-safe même en
     *   appels concurrents, car RxDB sérialise les writes par document) ;
     * - pas de mission active → on retire l'entrée pour ne pas resservir une
     *   valeur périmée hors-ligne.
     * Ne vide jamais la collection : les missions chargées (downloadMission) restent.
     */
    private async persistActiveMission(mission: MissionResponseDto | null): Promise<void> {
        try {
            const db = this.databaseService.getDb();
            if (mission?.id) {
                await db.missions.incrementalUpsert({
                    missionId: ACTIVE_MISSION_KEY,
                    mission,
                    savedAt: new Date().toISOString(),
                } as any);
            } else {
                const doc = await db.missions.findOne(ACTIVE_MISSION_KEY).exec();
                if (doc) await doc.remove();
            }
        } catch (err) {
            console.error('Error saving active mission locally', err);
        }
    }

    clearActiveMission(): void {
        this.activeMissionSubject.next(null);
    }

    get isLoggedIn(): boolean {
        const token = this.token;
        if (!token) return false;
        try {
            const payload = jwtDecode<DecodedToken>(token);
            const now = Math.floor(Date.now() / 1000);
            if (payload.exp && payload.exp < now) {
                this.token = null;
                return false;
            }
            return true;
        } catch {
            return false;
        }
    }

    get token(): string | null {
        return localStorage.getItem('token');
    }
    set token(token: string | null) {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    }
    
    clearToken():void{
        localStorage.removeItem('token');
    }
    /**
     * Vrai si le token est un token « offline » fabriqué localement
     * (claim `offline: true`, cf. createOfflineToken). Sert à ne jamais l'envoyer
     * au serveur : non signé, il y provoquerait une erreur (alg=none rejeté).
     */
    isOfflineToken(token: string | null = this.token): boolean {
        if (!token) return false;
        try {
            const payload = jwtDecode<DecodedToken & { offline?: boolean }>(token);
            return payload?.offline === true;
        } catch {
            return false;
        }
    }

    hasPermission(permission: string): boolean {
        const token = this.token;
        if (!token) return false;
        try {
            const decoded = jwtDecode<DecodedToken>(token);
            return decoded?.permissions?.includes(permission) ?? false;
        } catch {
            return false;
        }
    }

    hasRole(role: string): boolean {
        const token = this.token;
        if (!token) return false;
        try {
            const decoded = jwtDecode<DecodedToken>(token);
            return decoded?.roles?.includes(role) ?? false;
        } catch {
            return false;
        }
    }

    /**
     * Fabrique un JWT local (non signé) à partir d'un utilisateur stocké hors ligne.
     * Utilisé UNIQUEMENT en mode offline, quand aucun vrai JWT du serveur n'est
     * disponible. Contrairement à un simple UUID, ce token est décodable par
     * `jwtDecode`, donc compatible avec `isLoggedIn`, `hasRole`, `decodeToken` :
     * la navigation locale et l'affichage par rôle fonctionnent hors ligne.
     * Il n'a pas de signature valide côté serveur (usage local seulement) et pas
     * de `exp` : la session tient jusqu'à la déconnexion. Les permissions ne sont
     * pas stockées localement, elles restent donc vides hors ligne.
     */
    createOfflineToken(user: {
        username: string;
        first_name?: string;
        last_name?: string;
        email?: string;
        roles?: readonly string[];
    }): string {
        const header = { alg: 'none', typ: 'JWT' };
        const payload = {
            sub: user.username,
            first_name: user.first_name ?? '',
            last_name: user.last_name ?? '',
            email: user.email ?? '',
            roles: user.roles ? [...user.roles] : [],
            permissions: [] as string[],
            temporal: false,
            offline: true,
            iat: Math.floor(Date.now() / 1000),
        };
        return `${this.base64UrlEncode(header)}.${this.base64UrlEncode(payload)}.`;
    }

    private base64UrlEncode(obj: unknown): string {
        const bytes = new TextEncoder().encode(JSON.stringify(obj));
        let binary = '';
        bytes.forEach(b => (binary += String.fromCharCode(b)));
        return btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    decodeToken(token: string): UserDocumentType | null {
        try {
            const payload = jwtDecode<DecodedToken>(token);
        
            console.log('Decoded token payload:', payload);
            return {
                username: payload.sub || '',
                password: '',
                first_name: payload['first_name'] || '',
                last_name: payload['last_name'] || '',
                email: payload['email'] || '',
                roles: payload['roles'] || [],
            };
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
        

    }
}